import { accountService } from "@/app/services/AccountService";
import { applicationService } from "@/app/services/ApplicationService";
import { informationService } from "@/app/services/InformationService";
import { captureException } from "@/plugins/sentry";
import { Message, MessageId } from "@/types/fcm/message";
import { RouteLocationRaw } from "vue-router";
import router from "@/router";

/**
 * カスタムイベントを送信する
 * @link https://github.com/dpa99c/cordova-plugin-firebasex?tab=readme-ov-file#logevent
 * @param eventName イベント名
 * @param eventProperties イベントプロパティ
 */
export const sendFirebaseCustomEvent = (
  eventName: string,
  eventProperties: Record<string, string | number | boolean>
) => {
  try {
    // @ts-ignore
    if (window.FirebasePlugin === undefined) {
      return;
    }

    // @ts-ignore
    window.FirebasePlugin.logEvent(eventName, eventProperties);
  } catch (error) {
    // 後続処理は続行させたいので、エラーが発生した場合 Sentry への通知のみ実施する
    captureException(error as Error);
  }
};

/**
 * push通知を受け取った時
 * @param fcmMessage
 * @param instanceId
 * @param isBackground
 */
export async function onFcmReceived(
  fcmMessage: Message,
  instanceId: string,
  isBackground: boolean
) {
  // push通知を受け取った時点で通知一覧を更新
  await accountService.refreshAppBadgeCount();
  const type = fcmMessage.type_id;
  const message = fcmMessage.caisuke_message;
  const destination = fcmMessage.destination;

  await handleMessage(type, message, isBackground, instanceId, destination);
}

/**
 * プッシュ通知による画面遷移を処理する
 */
function locationByPush(
  route: RouteLocationRaw | string,
  message: string,
  isBackground: boolean
) {
  if (isBackground) {
    router.push(route);
  } else {
    // フォアグラウンド時は画面遷移確認ダイアログを出す。
    const result = window.confirm(message + "\n確認しますか？");
    if (result) {
      router.push(route);
    }
  }
}

/**
 * メッセージタイプに応じた処理を実行する
 */
async function handleMessage(
  type: MessageId,
  message: string,
  isBackground: boolean,
  instanceId: string,
  destination?: string
) {
  switch (type) {
    case "auth_reminder":
      return routeToMypage(message, isBackground);
    case "evaluated_by_office":
    case "remanded_attendance_correction":
      return routeToApplicationDetail(message, isBackground, instanceId);
    case "attendance_alert":
    case "leaving_work_alert": {
      const application = await applicationService.fetchApplication(
        Number(instanceId)
      );
      return routeToOrdersDetail(
        message,
        isBackground,
        String(application.order.id)
      );
    }
    case "order_opened":
      return routeToOrdersDetail(message, isBackground, instanceId);
    case "message_from_office":
      return routeToMessageRoomDetail(message, isBackground, instanceId);
    case "account_request_verified":
    case "account_request_rejected":
    case "qualification_request_rejected":
      return routeToMypageAccount(message, isBackground);
    case "qualification_request_verified":
    case "previous_day_reminder":
    case "recover_credit_score":
      return routeToSearch(message, isBackground);
    case "admin_information":
      // 通知を開いたことを記録
      await registerOpenPushNotificationEvent(instanceId);
      return routeToInformations(message, isBackground, destination);
    case "favorited_by_office":
      return routeToSearchByOffice(message, isBackground, instanceId);
    case "approved_attendance_correction":
      return routeToSalary(message, isBackground);
    case "rejected_attendance_correction":
      return routeToAttendanceCorrectionDetail(
        message,
        isBackground,
        instanceId
      );
    default:
      console.warn(`存在しないtype_idです: ${type}`);
      return;
  }
}

function routeToMypage(message: string, isBackground: boolean) {
  locationByPush(
    {
      name: "Mypage"
    },
    message,
    isBackground
  );
}

function routeToApplicationDetail(
  message: string,
  isBackground: boolean,
  instanceId: string
) {
  locationByPush(
    {
      name: "ApplicationsDetail",
      params: { applicationId: String(instanceId) }
    },
    message,
    isBackground
  );
}

function routeToOrdersDetail(
  message: string,
  isBackground: boolean,
  instanceId: string
) {
  locationByPush(
    {
      name: "OrdersDetail",
      params: { orderId: String(instanceId) }
    },
    message,
    isBackground
  );
}

function routeToMessageRoomDetail(
  message: string,
  isBackground: boolean,
  instanceId: string
) {
  locationByPush(
    {
      name: "MessageRoomDetail",
      params: { officeId: instanceId }
    },
    message,
    isBackground
  );
}

function routeToMypageAccount(message: string, isBackground: boolean) {
  locationByPush(
    {
      name: "MypageAccount"
    },
    message,
    isBackground
  );
}

function routeToSearch(message: string, isBackground: boolean) {
  locationByPush(
    {
      name: "Search"
    },
    message,
    isBackground
  );
}

async function registerOpenPushNotificationEvent(instanceId: string) {
  // イベントを記録
  const id = Number(instanceId);
  if (!isNaN(id)) {
    try {
      await informationService.openPushNotification(id);
    } catch (error) {
      // エラーが発生した場合はサイレントでSentryに通知
      captureException(error as Error);
    }
  }
}

function routeToInformations(
  message: string,
  isBackground: boolean,
  destination?: string
) {
  locationByPush(
    destination ? { path: destination } : { name: "Informations" },
    message,
    isBackground
  );
}

function routeToSearchByOffice(
  message: string,
  isBackground: boolean,
  instanceId: string
) {
  locationByPush(
    {
      name: "SearchByOffice",
      params: { officeId: instanceId }
    },
    message,
    isBackground
  );
}

function routeToSalary(message: string, isBackground: boolean) {
  locationByPush(
    {
      name: "Salary"
    },
    message,
    isBackground
  );
}

function routeToAttendanceCorrectionDetail(
  message: string,
  isBackground: boolean,
  instanceId: string
) {
  locationByPush(
    {
      name: "AttendanceCorrectionDetail",
      params: { applicationId: String(instanceId) }
    },
    message,
    isBackground
  );
}

export function initFirebasePlugin() {
  // @ts-ignore
  if (!window.FirebasePlugin) {
    console.warn("skipped: initFirebasePlugin");
    return;
  }
  // @ts-ignore
  FirebasePlugin.onMessageReceived(
    (fcmMessage: Message) => {
      // alert(JSON.stringify(fcmMessage));
      console.log("FIREBASE PLUGIN init end------------------");
      console.log("catched log", JSON.stringify(fcmMessage));
      console.log("Message type: " + fcmMessage.messageType);

      onFcmReceived(
        fcmMessage,
        fcmMessage.instance_id,
        !!fcmMessage.tap && fcmMessage.tap !== "foreground"
      );

      if (fcmMessage.messageType === "notification") {
        console.log("Notification message received");
        if (fcmMessage.tap) {
          console.log("Tapped in " + fcmMessage.tap);
        }
      }
    },
    (error: Error) => {
      captureException(error);
    }
  );
}
